import React, { useState } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import {
  PollWithQuestionsResponse,
  PollQuestionResponse,
  PollOptionResponse,
  PollQuestionType,
} from '../../../../../../api/types/settings/settings-all';
import { AddQuestionDialog } from './add-question-dialog';
import { EditQuestionDialog } from './edit-question-dialog';
import { EditOptionDialog } from './edit-option-dialog';
import { deleteQuestionOperation } from '../operations/delete-question-operation';
import { deleteOptionOperation } from '../operations/delete-option-operation';
import { updateQuestionsOrderOperation } from '../operations/update-questions-order-operation';
import globalSettingsStores from '../../../../../../stores/settings-stores';
import { observer } from 'mobx-react-lite';

interface PollDetailsDialogProps {
  poll: PollWithQuestionsResponse;
  closeDialog: () => void;
}

export const PollDetailsDialog: React.FC<PollDetailsDialogProps> = observer(
  ({ poll: initialPoll, closeDialog }) => {
    // Get the latest poll data from the store
    const poll =
      globalSettingsStores.abstractPolls.find((p) => p.id === initialPoll.id) ||
      initialPoll;

    const [expandedQuestions, setExpandedQuestions] = useState<number[]>([]);
    const [showAddQuestionDialog, setShowAddQuestionDialog] = useState(false);
    const [editQuestion, setEditQuestion] =
      useState<PollQuestionResponse | null>(null);
    const [editOption, setEditOption] = useState<{
      questionId: number;
      option: PollOptionResponse;
    } | null>(null);
    const [loadingDelete, setLoadingDelete] = useState(false);
    const [draggedQuestionId, setDraggedQuestionId] = useState<number | null>(
      null,
    );
    const [dragOverQuestionId, setDragOverQuestionId] = useState<number | null>(
      null,
    );
    const [loadingReorder, setLoadingReorder] = useState(false);

    const toggleQuestion = (questionId: number) => {
      setExpandedQuestions((prev) =>
        prev.includes(questionId)
          ? prev.filter((id) => id !== questionId)
          : [...prev, questionId],
      );
    };

    const handleDeleteQuestion = (question: PollQuestionResponse) => {
      confirmDialog({
        message: `האם אתה בטוח שברצונך למחוק את השאלה "${question.questionText}"?`,
        header: 'אישור מחיקה',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'מחק',
        rejectLabel: 'ביטול',
        acceptClassName: 'p-button-danger',
        accept: () => {
          deleteQuestionOperation(
            poll.id,
            question.id,
            setLoadingDelete,
            () => {
              // Question deleted successfully
            },
          );
        },
      });
    };

    const handleDeleteOption = (
      questionId: number,
      option: PollOptionResponse,
    ) => {
      confirmDialog({
        message: `האם אתה בטוח שברצונך למחוק את התשובה "${option.optionText}"?`,
        header: 'אישור מחיקה',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'מחק',
        rejectLabel: 'ביטול',
        acceptClassName: 'p-button-danger',
        accept: () => {
          deleteOptionOperation(
            poll.id,
            questionId,
            option.id,
            setLoadingDelete,
            () => {
              // Option deleted successfully
            },
          );
        },
      });
    };

    const getQuestionTypeLabel = (type: PollQuestionType) => {
      return type === PollQuestionType.multiple_choice
        ? 'בחירה מרובה'
        : 'טקסט חופשי';
    };

    const sortedQuestions = [...(poll.questions || [])].sort(
      (a, b) => a.questionOrder - b.questionOrder,
    );

    // Drag and Drop handlers
    const handleDragStart = (
      e: React.DragEvent<HTMLDivElement>,
      questionId: number,
    ) => {
      setDraggedQuestionId(questionId);
      e.dataTransfer.effectAllowed = 'move';
      // Store reference before setTimeout (React pools synthetic events)
      const element = e.currentTarget;
      setTimeout(() => {
        if (element) {
          element.style.opacity = '0.5';
        }
      }, 0);
    };

    const handleDragEnd = (e: React.DragEvent<HTMLDivElement>) => {
      if (e.currentTarget) {
        e.currentTarget.style.opacity = '1';
      }
      setDraggedQuestionId(null);
      setDragOverQuestionId(null);
    };

    const handleDragOver = (
      e: React.DragEvent<HTMLDivElement>,
      questionId: number,
    ) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      if (draggedQuestionId !== null && draggedQuestionId !== questionId) {
        setDragOverQuestionId(questionId);
      }
    };

    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
      // Only clear if we're leaving the element (not entering a child)
      const relatedTarget = e.relatedTarget as Node;
      if (!e.currentTarget.contains(relatedTarget)) {
        setDragOverQuestionId(null);
      }
    };

    const handleDrop = (
      e: React.DragEvent<HTMLDivElement>,
      targetQuestionId: number,
    ) => {
      e.preventDefault();
      setDragOverQuestionId(null);
      if (
        draggedQuestionId === null ||
        draggedQuestionId === targetQuestionId
      ) {
        return;
      }

      const draggedIndex = sortedQuestions.findIndex(
        (q) => q.id === draggedQuestionId,
      );
      const targetIndex = sortedQuestions.findIndex(
        (q) => q.id === targetQuestionId,
      );

      if (draggedIndex === -1 || targetIndex === -1) return;

      // Create new order
      const newOrder = [...sortedQuestions];
      const [draggedItem] = newOrder.splice(draggedIndex, 1);
      newOrder.splice(targetIndex, 0, draggedItem);

      // Show confirmation dialog
      confirmDialog({
        message: (
          <div style={{ textAlign: 'center' }}>
            <i
              className="pi pi-arrows-v"
              style={{
                fontSize: '2.5rem',
                color: '#3b82f6',
                marginBottom: '12px',
              }}
            />
            <p style={{ margin: '0 0 8px 0', fontWeight: 500 }}>
              האם אתה בטוח שברצונך לשנות את סדר השאלות?
            </p>
            <p style={{ margin: 0, color: '#6b7280', fontSize: '0.9rem' }}>
              השאלה "{sortedQuestions[draggedIndex].questionText}" תועבר למיקום{' '}
              {targetIndex + 1}
            </p>
          </div>
        ),
        header: 'שינוי סדר השאלות',
        icon: 'pi pi-question-circle',
        acceptLabel: 'אישור',
        rejectLabel: 'ביטול',
        acceptClassName: 'p-button-primary',
        accept: () => {
          const questionsOrder = newOrder.map((q, index) => ({
            questionId: q.id,
            orderItem: index,
          }));
          updateQuestionsOrderOperation(
            poll.id,
            questionsOrder,
            setLoadingReorder,
            () => {
              // Reorder successful
            },
          );
        },
      });

      setDraggedQuestionId(null);
    };

    return (
      <div style={{ direction: 'rtl' }}>
        <ConfirmDialog />

        {/* Add Question Dialog */}
        <Dialog
          header="הוספת שאלה חדשה"
          visible={showAddQuestionDialog}
          onHide={() => setShowAddQuestionDialog(false)}
          style={{
            width: '550px',
            backgroundColor: 'white',
            borderRadius: '8px',
          }}
        >
          <AddQuestionDialog
            pollId={poll.id}
            closeDialog={() => setShowAddQuestionDialog(false)}
          />
        </Dialog>

        {/* Edit Question Dialog */}
        <Dialog
          header="עריכת שאלה"
          visible={editQuestion !== null}
          onHide={() => setEditQuestion(null)}
          style={{
            width: '500px',
            backgroundColor: 'white',
            borderRadius: '8px',
          }}
        >
          {editQuestion && (
            <EditQuestionDialog
              pollId={poll.id}
              question={editQuestion}
              closeDialog={() => setEditQuestion(null)}
            />
          )}
        </Dialog>

        {/* Edit Option Dialog */}
        <Dialog
          header="עריכת תשובה"
          visible={editOption !== null}
          onHide={() => setEditOption(null)}
          style={{
            width: '500px',
            backgroundColor: 'white',
            borderRadius: '8px',
          }}
        >
          {editOption && (
            <EditOptionDialog
              pollId={poll.id}
              questionId={editOption.questionId}
              option={editOption.option}
              closeDialog={() => setEditOption(null)}
            />
          )}
        </Dialog>

        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px',
            paddingBottom: '16px',
            borderBottom: '1px solid #e5e7eb',
          }}
        >
          <div>
            <h3 style={{ margin: 0, color: '#1f2937' }}>{poll.pollName}</h3>
            <p
              style={{
                margin: '4px 0 0',
                color: '#6b7280',
                fontSize: '0.9rem',
              }}
            >
              {sortedQuestions.length} שאלות
            </p>
          </div>
          <Button
            label="הוספת שאלה"
            icon="pi pi-plus"
            onClick={() => setShowAddQuestionDialog(true)}
            style={{ height: '36px' }}
          />
        </div>

        {/* Questions List */}
        {sortedQuestions.length === 0 ? (
          <div
            style={{
              textAlign: 'center',
              padding: '40px',
              backgroundColor: '#f9fafb',
              borderRadius: '8px',
            }}
          >
            <i
              className="pi pi-inbox"
              style={{ fontSize: '3rem', color: '#ccc', marginBottom: '16px' }}
            />
            <p style={{ color: '#888' }}>אין שאלות במשוב זה</p>
            <Button
              label="הוסף שאלה ראשונה"
              icon="pi pi-plus"
              onClick={() => setShowAddQuestionDialog(true)}
              className="p-button-outlined"
              style={{ marginTop: '16px' }}
            />
          </div>
        ) : (
          <div
            style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}
          >
            {sortedQuestions.map((question, index) => {
              const isExpanded = expandedQuestions.includes(question.id);
              const sortedOptions = [...(question.options || [])].sort(
                (a, b) => a.optionOrder - b.optionOrder,
              );
              const isDragging = draggedQuestionId === question.id;
              const isDragOver =
                dragOverQuestionId === question.id &&
                draggedQuestionId !== question.id;

              return (
                <div
                  key={question.id}
                  draggable={!loadingReorder && !loadingDelete}
                  onDragStart={(e) => handleDragStart(e, question.id)}
                  onDragEnd={handleDragEnd}
                  onDragOver={(e) => handleDragOver(e, question.id)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, question.id)}
                  style={{
                    border: isDragging
                      ? '2px dashed #3b82f6'
                      : isDragOver
                        ? '2px solid #10b981'
                        : '1px solid #e5e7eb',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    backgroundColor: isDragging
                      ? '#eff6ff'
                      : isDragOver
                        ? '#ecfdf5'
                        : 'white',
                    transition: 'all 0.2s ease',
                    cursor: loadingReorder ? 'wait' : 'grab',
                    transform: isDragOver ? 'scale(1.02)' : 'scale(1)',
                    boxShadow: isDragOver
                      ? '0 4px 12px rgba(16, 185, 129, 0.3)'
                      : 'none',
                  }}
                >
                  {/* Drop indicator */}
                  {isDragOver && (
                    <div
                      style={{
                        backgroundColor: '#10b981',
                        color: 'white',
                        padding: '4px 12px',
                        fontSize: '12px',
                        fontWeight: 500,
                        textAlign: 'center',
                      }}
                    >
                      <i
                        className="pi pi-arrow-down"
                        style={{ marginLeft: '6px' }}
                      />
                      שחרר כאן להעברת השאלה למיקום זה
                    </div>
                  )}
                  {/* Question Header */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '12px 16px',
                      backgroundColor: isExpanded ? '#f3f4f6' : '#fafafa',
                      cursor: loadingReorder ? 'wait' : 'pointer',
                      transition: 'background-color 0.2s',
                    }}
                    onClick={() => toggleQuestion(question.id)}
                  >
                    {/* Drag Handle */}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginLeft: '8px',
                        cursor: loadingReorder ? 'wait' : 'grab',
                        color: '#9ca3af',
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <i className="pi pi-bars" style={{ fontSize: '14px' }} />
                    </div>
                    <span
                      style={{
                        width: '28px',
                        height: '28px',
                        borderRadius: '50%',
                        backgroundColor: '#3b82f6',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        marginLeft: '12px',
                      }}
                    >
                      {index + 1}
                    </span>
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontWeight: 500,
                          color: '#1f2937',
                          marginBottom: '2px',
                        }}
                      >
                        {question.questionText}
                      </div>
                      <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                        {getQuestionTypeLabel(question.questionType)}
                        {question.questionType ===
                          PollQuestionType.multiple_choice &&
                          ` • ${sortedOptions.length} תשובות`}
                      </div>
                    </div>
                    <div
                      style={{ display: 'flex', gap: '4px', marginLeft: '8px' }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Button
                        icon="pi pi-pencil"
                        className="p-button-rounded p-button-text p-button-warning"
                        onClick={() => setEditQuestion(question)}
                        style={{ width: '32px', height: '32px' }}
                        tooltip="עריכת שאלה"
                        tooltipOptions={{ position: 'top' }}
                        disabled={loadingDelete}
                      />
                      <Button
                        icon="pi pi-trash"
                        className="p-button-rounded p-button-text p-button-danger"
                        onClick={() => handleDeleteQuestion(question)}
                        style={{ width: '32px', height: '32px' }}
                        tooltip="מחיקת שאלה"
                        tooltipOptions={{ position: 'top' }}
                        disabled={loadingDelete}
                      />
                    </div>
                    <i
                      className={`pi ${isExpanded ? 'pi-chevron-up' : 'pi-chevron-down'}`}
                      style={{
                        color: '#9ca3af',
                        transition: 'transform 0.2s',
                      }}
                    />
                  </div>

                  {/* Question Options (Expanded) */}
                  {isExpanded && (
                    <div
                      style={{
                        padding: '16px',
                        borderTop: '1px solid #e5e7eb',
                        backgroundColor: 'white',
                      }}
                    >
                      {question.questionType === PollQuestionType.free_text ? (
                        <div
                          style={{
                            padding: '16px',
                            backgroundColor: '#f9fafb',
                            borderRadius: '6px',
                            color: '#6b7280',
                            textAlign: 'center',
                          }}
                        >
                          <i
                            className="pi pi-align-left"
                            style={{ marginLeft: '8px' }}
                          />
                          שאלה זו היא שאלת טקסט חופשי - התלמיד יזין תשובה בכתב
                        </div>
                      ) : sortedOptions.length === 0 ? (
                        <div
                          style={{
                            padding: '16px',
                            backgroundColor: '#fef3c7',
                            borderRadius: '6px',
                            color: '#92400e',
                            textAlign: 'center',
                          }}
                        >
                          <i
                            className="pi pi-exclamation-triangle"
                            style={{ marginLeft: '8px' }}
                          />
                          אין תשובות לשאלה זו
                        </div>
                      ) : (
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '8px',
                          }}
                        >
                          {sortedOptions.map((option, optIndex) => (
                            <div
                              key={option.id}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: '10px 12px',
                                backgroundColor: '#f9fafb',
                                borderRadius: '6px',
                                border: '1px solid #e5e7eb',
                              }}
                            >
                              <span
                                style={{
                                  width: '22px',
                                  height: '22px',
                                  borderRadius: '50%',
                                  backgroundColor: '#10b981',
                                  color: 'white',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '11px',
                                  fontWeight: 'bold',
                                  marginLeft: '10px',
                                }}
                              >
                                {optIndex + 1}
                              </span>
                              <span style={{ flex: 1, color: '#374151' }}>
                                {option.optionText}
                              </span>
                              <span
                                style={{
                                  backgroundColor: '#dbeafe',
                                  color: '#1e40af',
                                  padding: '2px 8px',
                                  borderRadius: '4px',
                                  fontSize: '11px',
                                  fontWeight: 500,
                                  marginLeft: '8px',
                                }}
                              >
                                ערך: {option.optionValue}
                              </span>
                              <div style={{ display: 'flex', gap: '4px' }}>
                                <Button
                                  icon="pi pi-pencil"
                                  className="p-button-rounded p-button-text p-button-warning"
                                  onClick={() =>
                                    setEditOption({
                                      questionId: question.id,
                                      option,
                                    })
                                  }
                                  style={{ width: '28px', height: '28px' }}
                                  tooltip="עריכת תשובה"
                                  tooltipOptions={{ position: 'top' }}
                                  disabled={loadingDelete}
                                />
                                <Button
                                  icon="pi pi-trash"
                                  className="p-button-rounded p-button-text p-button-danger"
                                  onClick={() =>
                                    handleDeleteOption(question.id, option)
                                  }
                                  style={{ width: '28px', height: '28px' }}
                                  tooltip="מחיקת תשובה"
                                  tooltipOptions={{ position: 'top' }}
                                  disabled={loadingDelete}
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Footer */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginTop: '24px',
            paddingTop: '16px',
            borderTop: '1px solid #e5e7eb',
          }}
        >
          <Button
            label="סגור"
            icon="pi pi-times"
            className="p-button-outlined p-button-secondary"
            onClick={closeDialog}
          />
        </div>
      </div>
    );
  },
);
